import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook for managing push notifications
 * Handles subscription, permission, and notification sending
 */
export function usePushNotifications(token) {
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [registration, setRegistration] = useState(null);

  // Check browser support
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 
                      'PushManager' in window && 
                      'Notification' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Register service worker
  useEffect(() => {
    if (!isSupported) return;

    navigator.serviceWorker.register('/sw-push.js')
      .then((reg) => {
        console.log('Push SW registered:', reg);
        setRegistration(reg);
        
        // Check for existing subscription
        return reg.pushManager.getSubscription();
      })
      .then((sub) => {
        if (sub) {
          setSubscription(sub);
        }
      })
      .catch((err) => {
        console.error('SW registration failed:', err);
        setError('Failed to register service worker');
      });
  }, [isSupported]);

  // Convert VAPID key to Uint8Array
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Request notification permission and subscribe
  const subscribe = useCallback(async () => {
    if (!isSupported || !registration || !token) {
      setError('Push notifications not available');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);

      if (perm !== 'granted') {
        setError('Notification permission denied');
        setIsLoading(false);
        return null;
      }

      // Get VAPID public key from server
      const keyResponse = await axios.get(`${API_URL}/api/push-notifications/vapid-public-key`);
      const vapidPublicKey = keyResponse.data.public_key;

      // Subscribe to push manager
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server
      const subJson = sub.toJSON();
      await axios.post(
        `${API_URL}/api/push-notifications/subscribe`,
        {
          endpoint: subJson.endpoint,
          keys: {
            p256dh: subJson.keys.p256dh,
            auth: subJson.keys.auth
          },
          device_name: navigator.userAgent.includes('Mobile') ? 'Mobile Browser' : 'Desktop Browser'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSubscription(sub);
      setIsLoading(false);
      return sub;
    } catch (err) {
      console.error('Subscribe error:', err);
      setError(err.response?.data?.detail || 'Failed to enable notifications');
      setIsLoading(false);
      return null;
    }
  }, [isSupported, registration, token]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscription || !token) return false;

    setIsLoading(true);
    setError(null);

    try {
      // Unsubscribe from browser
      await subscription.unsubscribe();

      // Remove from server
      await axios.delete(`${API_URL}/api/push-notifications/unsubscribe`, {
        params: { endpoint: subscription.endpoint },
        headers: { Authorization: `Bearer ${token}` }
      });

      setSubscription(null);
      setIsLoading(false);
      return true;
    } catch (err) {
      console.error('Unsubscribe error:', err);
      setError(err.response?.data?.detail || 'Failed to disable notifications');
      setIsLoading(false);
      return false;
    }
  }, [subscription, token]);

  // Send a test notification
  const sendTest = useCallback(async () => {
    if (!token) return false;

    try {
      const response = await axios.post(
        `${API_URL}/api/push-notifications/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.success;
    } catch (err) {
      console.error('Test notification error:', err);
      setError(err.response?.data?.detail || 'Failed to send test notification');
      return false;
    }
  }, [token]);

  return {
    isSupported,
    permission,
    subscription,
    isSubscribed: !!subscription,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    sendTest
  };
}

export default usePushNotifications;
